/* eslint-disable better-mutation/no-mutation */

import { type PayloadAction, createSlice } from '@reduxjs/toolkit';
import {
    getSelectedShippingOptions,
    getSelectedShippingProducts,
} from './helpers/shipping-reducer-helpers';
import { v4 as uuidv4 } from 'uuid';
import type {
    ProductsPerWeightSpanAndSize,
    ShippingOption,
    ShippingProduct,
} from 'tradera/state/services/types/webapi-selling-generated';

const getUuid = () => uuidv4();

const errors = {
    noShipping: 'syi_validation_shipping_no_shipping',
    empty: 'syi_validation_shipping_enter_method_and_cost',
    invalid: 'syi_validation_shipping_method_invalid',
    duplicate: 'syi_validation_shipping_method_identical',
    weight: 'syi_validation_shipping_enter_weight',
};

function validate(newState: ShippingState) {
    if (!newState.offerTakeaway && !newState.offerShipping) {
        newState.globalValidationError = errors.noShipping;
        newState.validationErrors = {
            global: errors.noShipping,
        };
        return newState;
    }

    newState.globalValidationError = null;
    newState.validationErrors = {};

    return newState;
}

const offersFreeShipping = (shippingOptions: ShippingOption[]) =>
    shippingOptions.some((shippingOption) => shippingOption.cost === 0);

export type DecoratedShippingProduct = ShippingProduct & {
    cost: number | null;
    productId: number;
    type: string;
    uuid: string;
    outage?: boolean;
};

type ShippingState = {
    validationErrors: Record<string, string>;
    globalValidationError: string | null;
    shippingByWeightGroupedBySizes: ProductsPerWeightSpanAndSize[] | undefined;
    selectedShippingOptions: DecoratedShippingProduct[];
    selectedShippingSize: string | null;
    selectedShippingWeight: number | null;
    shippingOptionsLoading: boolean;
    shippingOptionsError: boolean;
    offerTakeaway: boolean;
    offerShipping: boolean;
    offersFreeShipping: boolean;
    fromCountry: string | null;
    packagingCost: number | null;
    decoratedShippingProducts: DecoratedShippingProduct[];
};

const initialState: ShippingState = validate({
    validationErrors: {},
    globalValidationError: null,
    selectedShippingOptions: [],
    selectedShippingSize: null,
    selectedShippingWeight: null,
    shippingOptionsLoading: false,
    shippingOptionsError: false,
    offerTakeaway: false,
    offerShipping: false,
    offersFreeShipping: false,
    fromCountry: null,
    packagingCost: null,
    shippingByWeightGroupedBySizes: [],
    decoratedShippingProducts: [],
});

const shippingContainsTakeaway = (shippingOptions: ShippingOption[]) =>
    shippingOptions.some(
        (shippingOption) =>
            shippingOption.type === 'Takeaway' ||
            shippingOption.type === 'TakeawayOnly',
    );

const shippingContainsNonTakeaway = (shippingOptions: ShippingOption[]) =>
    shippingOptions.some(
        (shippingOption) =>
            shippingOption.type !== 'Takeaway' &&
            shippingOption.type !== 'TakeawayOnly',
    );

export const mapToDecoratedShippingProduct = (
    shippingProduct: ShippingProduct,
): DecoratedShippingProduct => ({
    ...shippingProduct,
    cost: shippingProduct.price,
    productId: shippingProduct.id,
    type: shippingProduct.providerId || '',
    uuid: getUuid(),
});

const flatMapProductsPerWeightSpanAndSizeToDecoratedShippingProducts = (
    productsPerWeightSpanAndSize:
        | ProductsPerWeightSpanAndSize[]
        | null
        | undefined,
): DecoratedShippingProduct[] =>
    productsPerWeightSpanAndSize
        ?.flatMap((weightClass) =>
            weightClass.productsBySize?.flatMap((size) => size.products),
        )
        .filter((x) => !!x)
        .map(mapToDecoratedShippingProduct) ?? [];

const shippingSlice = createSlice({
    name: 'shipping',
    initialState,
    reducers: {
        resetSelectedShippingOptions: (state) => {
            state.selectedShippingOptions = [];
            state.offerShipping = false;
            state.offerTakeaway = false;
            state.selectedShippingSize = null;
            return validate(state);
        },
        setShippingOptionsByWeight: (
            state,
            action: PayloadAction<{
                productsPerWeightSpanAndSize:
                    | ProductsPerWeightSpanAndSize[]
                    | null
                    | undefined;
                shippingOptions: ShippingOption[];
            }>,
        ) => {
            const { productsPerWeightSpanAndSize, shippingOptions } =
                action.payload;

            const decoratedShippingProducts =
                flatMapProductsPerWeightSpanAndSizeToDecoratedShippingProducts(
                    productsPerWeightSpanAndSize,
                );

            state.decoratedShippingProducts = decoratedShippingProducts;
            state.shippingByWeightGroupedBySizes =
                productsPerWeightSpanAndSize ?? undefined;

            state.offerTakeaway = shippingContainsTakeaway(shippingOptions);
            state.offerShipping = shippingContainsNonTakeaway(shippingOptions);

            state.selectedShippingOptions = getSelectedShippingOptions(
                decoratedShippingProducts,
                shippingOptions,
                state.offerTakeaway,
            );
            state.offersFreeShipping = offersFreeShipping(
                state.selectedShippingOptions,
            );

            state.shippingOptionsLoading = false;
            state.shippingOptionsError = false;
            return validate(state);
        },
        autoSelectShippingOptions: (
            state,
            action: PayloadAction<number | undefined>,
        ) => {
            // only use autoSelect if no shipping options are selected, otherwise
            // we might overwrite selected shipping options from a template or resell
            if (state.selectedShippingOptions.length > 0) return;

            const autoSelectedWeight =
                state.shippingByWeightGroupedBySizes?.find(
                    (weightClass) => weightClass.autoSelect,
                );
            if (autoSelectedWeight) {
                state.selectedShippingWeight = autoSelectedWeight.weight;
                const autoSelectedSize =
                    autoSelectedWeight.productsBySize?.find(
                        (size) => size.autoSelect,
                    );
                if (autoSelectedSize) {
                    state.selectedShippingSize =
                        autoSelectedSize.sizeCategory ?? null;
                    state.selectedShippingOptions =
                        autoSelectedSize.products?.map((p) => ({
                            ...mapToDecoratedShippingProduct(p),
                            cost: p.price + (action.payload ?? 0),
                        })) ?? [];
                    if (state.selectedShippingOptions.length > 0) {
                        state.packagingCost = action.payload ?? 0;
                    }
                }
            }

            state.offerTakeaway = shippingContainsTakeaway(
                state.selectedShippingOptions,
            );
            state.offerShipping = shippingContainsNonTakeaway(
                state.selectedShippingOptions,
            );
            return validate(state);
        },

        setShippingOptionsLoading: (state, action: PayloadAction<boolean>) => {
            state.shippingOptionsLoading = action.payload;
            return validate(state);
        },
        setShippingOptionSelected: (
            state,
            action: PayloadAction<DecoratedShippingProduct[]>,
        ) => {
            const { payload } = action;

            const selectedOptions = getSelectedShippingProducts(
                state.decoratedShippingProducts,
                payload,
                state.offerTakeaway,
            );

            state.offerShipping = shippingContainsNonTakeaway(selectedOptions);
            state.offersFreeShipping = offersFreeShipping(selectedOptions);
            state.selectedShippingOptions = selectedOptions;

            return validate(state);
        },
        setSelectedShippingSize: (
            state,
            action: PayloadAction<typeof initialState.selectedShippingSize>,
        ) => {
            state.selectedShippingSize = action.payload;
            return validate(state);
        },
        setPackagingCost: (state, action: PayloadAction<number | null>) => {
            state.packagingCost = action.payload;
            return validate(state);
        },
        setTakeawaySelected: (
            state,
            action: PayloadAction<{ offerTakeaway: boolean }>,
        ) => {
            state.offerTakeaway = action.payload.offerTakeaway;
            state.selectedShippingOptions = getSelectedShippingProducts(
                state.decoratedShippingProducts,
                state.selectedShippingOptions,
                state.offerTakeaway,
            );

            return validate(state);
        },
        setShippingOptionsError: (state) => {
            state.shippingOptionsLoading = false;
            state.shippingOptionsError = true;

            return validate(state);
        },
        setShippingFromCountry: (
            state,
            action: PayloadAction<{ fromCountry: string }>,
        ) => {
            state.fromCountry = action.payload.fromCountry;
        },
        setSelectedShippingWeight: (
            state,
            action: PayloadAction<number | null>,
        ) => {
            state.selectedShippingWeight = action.payload;
            return validate(state);
        },
    },
});

export const {
    autoSelectShippingOptions,
    setShippingFromCountry,
    setShippingOptionsByWeight,
    setShippingOptionsError,
    setTakeawaySelected,
    setShippingOptionSelected,
    resetSelectedShippingOptions,
    setShippingOptionsLoading,
    setSelectedShippingSize,
    setPackagingCost,
    setSelectedShippingWeight,
} = shippingSlice.actions;

export default shippingSlice.reducer;
